import React, { useState, useEffect, useRef } from 'react';
import Styles from './styles.module.css';
import {
  IoSparklesOutline, IoBookOutline, IoNewspaperOutline, IoImagesOutline,
} from "react-icons/io5";
import MatchShot from './../../../image/home/students/match.png';
import StudyShot from './../../../image/home/students/study.png';
import ArticleShot from './../../../image/home/students/article.png';
import StyleShot from './../../../image/home/students/style.png';

/* ============================================================
   学生側の継続利用（サロンの業務効率とは別軸の差別化）
     左：見出し＋「開く理由」4つのリスト（常時表示）
     右：スマホ画面。3秒ごとに1項目ずつ自動送り（ホバー/フォーカス/画面外/reduced-motion で停止）
   ※ 国家試験対策・就活相談は提供予定
   ============================================================ */

type Kind = 'match' | 'study' | 'article' | 'style';

const ITEMS: { icon: JSX.Element; title: string; note: string; kind: Kind; shot: string }[] = [
  { icon: <IoSparklesOutline />, title: '性格診断・マッチング', note: '自分に合うサロンを知る', kind: 'match', shot: MatchShot },
  { icon: <IoBookOutline />, title: '国家試験対策', note: '日々の学習にも使える', kind: 'study', shot: StudyShot },
  { icon: <IoNewspaperOutline />, title: '先輩美容師の記事', note: '働き方やキャリアを知る', kind: 'article', shot: ArticleShot },
  { icon: <IoImagesOutline />, title: '作品・スタイル', note: '美容のトレンドや作品を見る', kind: 'style', shot: StyleShot },
];

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);
  return reduced;
};

const Screen: React.FC<{ item: typeof ITEMS[number]; k: number }> = ({ item, k }) => (
  <div key={k} className={Styles.scrInner}>
    <img className={Styles.screenShot} src={item.shot} alt={`${item.title}のスマホ画面`} />
  </div>
);

const INTERVAL = 3000;

const HomeStudents = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(true);
  const reduced = usePrefersReducedMotion();
  const wrapRef = useRef<HTMLDivElement>(null);

  const running = !paused && inView && !reduced;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // reduced-motion では自動送りしない代わりに、タイマーで進める
  useEffect(() => {
    if (!running || !reduced) return;
    const id = setTimeout(() => setActive((a) => (a + 1) % ITEMS.length), INTERVAL);
    return () => clearTimeout(id);
  }, [running, reduced, active]);

  const next = () => setActive((a) => (a + 1) % ITEMS.length);

  const onListKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      setActive((a) => (a + 1) % ITEMS.length);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      setActive((a) => (a - 1 + ITEMS.length) % ITEMS.length);
    }
  };

  return (
    <div className={Styles.students}>
      <div
        className={Styles.inner}
        ref={wrapRef}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
      >
        <div className={Styles.copy}>
          <span className={Styles.kicker}>For students</span>
          <h2>
            求人を探すだけじゃない。<br />
            学生生活の中に、開く理由を。
          </h2>
          <p className={Styles.lead}>
            国家試験、性格診断、先輩の記事、作品・スタイル。学生生活の中で日常的に使えるコンテンツがあるから、求人情報との接点も自然に生まれます。
          </p>

          <div className={Styles.list} role="tablist" aria-label="学生がアプリを開く理由" onKeyDown={onListKey}>
            {ITEMS.map((it, i) => {
              const on = i === active;
              return (
                <button
                  key={it.title}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  tabIndex={on ? 0 : -1}
                  className={`${Styles.item} ${on ? Styles.itemOn : ''}`}
                  onClick={() => setActive(i)}
                >
                  <span className={Styles.itemIcon}>{it.icon}</span>
                  <span className={Styles.itemText}>
                    <span className={Styles.itemTitle}>{it.title}</span>
                    {on && <span className={Styles.itemNote}>{it.note}</span>}
                  </span>
                  {on && !reduced && (
                    <span className={Styles.prog}>
                      <span
                        key={active}
                        className={Styles.progFill}
                        style={{ animationPlayState: running ? 'running' : 'paused' }}
                        onAnimationEnd={next}
                      />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <p className={Styles.note}>※ 国家試験対策・就活相談は提供予定です。</p>
        </div>

        <div className={Styles.stage}>
          <span className={Styles.stageGlow} aria-hidden="true" />
            <div className={Styles.phone}>
              <span className={Styles.handle} aria-hidden="true" />
              <div className={Styles.screen} role="tabpanel" aria-label={ITEMS[active].title}>
                <Screen item={ITEMS[active]} k={active} />
              </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default HomeStudents;
